import { useState, useEffect, useMemo } from 'react';
import { Rocket, Globe, AlertTriangle, CheckCircle, PlusCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { usePromoteFunnelToProduction } from '@/lib/api/funnels';
import { useCustomDomainsList } from '@/lib/api/domains';
import { Storefront } from '../types';
import { trpc } from '@/lib/api/trpc';
import { DryRunSandboxWarning } from './DryRunSandboxWarning';

interface PromoteToProductionDialogProps {
  funnel: Storefront;
  storeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onSwitchToProduction?: () => void; // Callback to switch view to production mode
}

export function PromoteToProductionDialog({
  funnel,
  storeId,
  open,
  onOpenChange,
  onSuccess,
  onSwitchToProduction,
}: PromoteToProductionDialogProps) {
  const { t } = useTranslation('storefront');
  const [customDomain, setCustomDomain] = useState('');
  const [result, setResult] = useState<{ stagingUrl: string; productionUrl: string } | null>(null);
  const [conflictWarning, setConflictWarning] = useState<{ funnelName: string; funnelId: string } | null>(
    null,
  );
  const [acknowledgeConflict, setAcknowledgeConflict] = useState(false);

  // Check if funnel is already promoted
  const existingDomains = (funnel.productionDomains as string[]) || [];
  const isAlreadyPromoted = existingDomains.length > 0;
  const lockedDomain = isAlreadyPromoted ? existingDomains[0] : null;

  const promoteMutation = usePromoteFunnelToProduction();
  // Both queries are gated on `open`: the enabled false->true flip fetches
  // when the cache is stale, so no manual refetch-on-open is needed — the old
  // effect double-fetched every open (audit FE-15). staleTime keeps reopens
  // within 30s on cache (and prevents refetch churn during interaction).
  const {
    data: domainsData,
    isLoading: isLoadingDomains,
  } = useCustomDomainsList({
    enabled: open,
  });

  // Fetch all funnels to check which domains are in use
  const { data: allFunnels } = trpc.funnels.list.useQuery(
    { storeId },
    {
      enabled: open,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 30000, // Prevent refetch during interaction
    },
  );

  // Check for domain conflicts using dedicated endpoint
  const { data: conflictCheck } = trpc.funnels.checkDomainConflict.useQuery(
    {
      storeId,
      customDomain: customDomain || '',
      excludeFunnelId: funnel.id,
    },
    {
      enabled: open && !!customDomain,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 10000, // Prevent immediate refetch on domain change
    },
  );

  // Reset dialog state when it opens (data freshness is handled by the
  // enabled-gated queries above)
  useEffect(() => {
    if (open) {
      if (lockedDomain) {
        setCustomDomain(lockedDomain);
      }
      setConflictWarning(null); // Reset warning when dialog opens
    }
  }, [open, lockedDomain]);

  // Update conflict warning based on backend check
  useEffect(() => {
    if (!customDomain || !conflictCheck) {
      setConflictWarning(null);
      setAcknowledgeConflict(false);
      return;
    }

    if (conflictCheck.hasConflict && conflictCheck.conflictingFunnel) {
      setConflictWarning({
        funnelName: conflictCheck.conflictingFunnel.name || 'Unnamed Funnel',
        funnelId: conflictCheck.conflictingFunnel.id,
      });
      setAcknowledgeConflict(false);
    } else {
      setConflictWarning(null);
      setAcknowledgeConflict(false);
    }
  }, [customDomain, conflictCheck]);

  // Memoize verified domains with their usage status to prevent re-renders during selection
  const domainOptions = useMemo(() => {
    const verifiedDomains = domainsData?.domains?.filter((d) => d.isVerified && d.enabled) || [];

    return verifiedDomains.map((domain) => {
      const usingFunnel = allFunnels?.find((f: any) => {
        if (f.id === funnel.id) return false;
        const domains = (f.productionDomains as string[]) || [];
        return domains.includes(domain.domain);
      });

      return {
        id: domain.id,
        domain: domain.domain,
        inUseBy: usingFunnel?.name || null,
      };
    });
  }, [domainsData?.domains, allFunnels, funnel.id]);

  const handlePromote = async () => {
    if (!customDomain) {
      toast.error(t('promote.selectDomainError'));
      return;
    }

    // Check if there's a conflict that wasn't acknowledged
    if (conflictWarning && !acknowledgeConflict) {
      toast.error(t('promote.acknowledgeConflictError'));
      return;
    }

    try {
      const res = await promoteMutation.mutateAsync({
        funnelId: funnel.id,
        storeId,
        customDomain,
        acknowledgeConflict: acknowledgeConflict, // Pass acknowledgment to backend
      });

      setResult({
        stagingUrl: res.stagingUrl,
        productionUrl: res.productionUrl,
      });
      toast.success(t('promote.promotedSuccess'));
      if (onSuccess) onSuccess();
      if (onSwitchToProduction) onSwitchToProduction(); // Switch to production view
    } catch (error) {
      console.error('Failed to promote:', error);
      // Surface the server's reason (promote-time validation, domain errors)
      // instead of a bare generic toast (FE-11) — same extraction as
      // useFunnelSave's save-error path.
      const err = error as { message?: string; data?: { message?: string }; shape?: { message?: string } };
      const serverMessage = err?.message || err?.data?.message || err?.shape?.message;
      toast.error(
        serverMessage ? `${t('promote.promoteFailed')}: ${serverMessage}` : t('promote.promoteFailed'),
        { duration: 8000 },
      );
    }
  };

  const handleClose = () => {
    if (!promoteMutation.isPending) {
      setResult(null);
      setCustomDomain('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Rocket className="h-4 w-4 text-primary" />
            {t('promote.title')}
          </DialogTitle>
          <DialogDescription className="text-xs">{t('promote.description')}</DialogDescription>
        </DialogHeader>

        <DryRunSandboxWarning funnel={funnel} storeId={storeId}>
        {!result ? (
          <div className="space-y-3 py-2">
            {/* Show locked domain message if already promoted */}
            {isAlreadyPromoted && lockedDomain && (
              <Alert className="border-primary/30 bg-primary/10 py-2 dark:border-primary/30 dark:bg-primary/10">
                <Globe className="h-3.5 w-3.5 text-primary" />
                <AlertDescription className="text-[11px] leading-relaxed text-primary dark:text-primary">
                  {t('promote.liveOnDomain', { domain: lockedDomain })}
                  <br />
                  {t('promote.demoteFirst')}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="domain-select" className="text-xs font-medium">
                {t('promote.customDomain')} {isAlreadyPromoted && t('promote.locked')}
              </Label>
              {isLoadingDomains ? (
                <div className="h-9 w-full animate-pulse rounded-md bg-slate-100 dark:bg-slate-800" />
              ) : (
                <Select value={customDomain} onValueChange={setCustomDomain} disabled={isAlreadyPromoted}>
                  <SelectTrigger id="domain-select" className="h-9">
                    <SelectValue placeholder={t('promote.selectDomain')} />
                  </SelectTrigger>
                  <SelectContent>
                    {domainOptions.length > 0 ? (
                      domainOptions.map((domain) => (
                        <SelectItem key={domain.id} value={domain.domain}>
                          <div className="flex items-center gap-2">
                            <Globe className="h-3 w-3 text-slate-500" />
                            <span className="text-xs">{domain.domain}</span>
                            {domain.inUseBy && (
                              <Badge
                                variant="outline"
                                className="ml-auto border-orange-300 text-[9px] text-orange-600"
                              >
                                {t('promote.inUse')}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-3 text-center">
                        <p className="text-muted-foreground mb-2 text-xs">{t('promote.noVerifiedDomains')}</p>
                        <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                          <a href="/settings/domains" target="_blank">
                            <PlusCircle className="mr-1 h-3 w-3" />
                            {t('promote.addDomain')}
                          </a>
                        </Button>
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Conflict Warning */}
            {conflictWarning && (
              <Alert className="border-red-200 bg-red-50 py-2 dark:border-red-900 dark:bg-red-900/20">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-xs font-semibold text-red-800 dark:text-red-200">
                  {t('promote.domainAlreadyInUse')}
                </AlertTitle>
                <AlertDescription className="mt-2 space-y-2 text-[11px] leading-relaxed text-red-700 dark:text-red-300">
                  <p>
                    {t('promote.domainUsedBy', { domain: customDomain, funnel: conflictWarning.funnelName })}
                  </p>
                  <p className="border-l-2 border-red-400 pl-2">
                    {t('promote.demoteWarning')}
                    <br />
                    {t('promote.onlyThisFunnelLive')}
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded bg-red-100/50 p-2 dark:bg-red-900/20">
                    <Checkbox
                      id="acknowledge-conflict"
                      checked={acknowledgeConflict}
                      onCheckedChange={(checked) => setAcknowledgeConflict(checked as boolean)}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor="acknowledge-conflict"
                      className="cursor-pointer text-[11px] font-semibold leading-tight text-red-900 dark:text-red-100"
                    >
                      {t('promote.acknowledgeConflict', { funnel: conflictWarning.funnelName })}
                    </label>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Alert className="py-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              <AlertDescription className="text-[11px] leading-relaxed">
                {t('promote.stagingNotice')}
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="min-w-0 space-y-3 py-2">
            <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-2.5 dark:border-green-900 dark:bg-green-900/20">
              <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-600" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-green-800 dark:text-green-200">
                  {t('promote.deployedSuccessfully')}
                </p>
                <p className="text-[10px] text-green-700 dark:text-green-300">{t('promote.funnelNowLive')}</p>
              </div>
            </div>

            <div className="min-w-0 space-y-2">
              <div className="min-w-0 overflow-hidden rounded-md border p-2">
                <div className="mb-0.5 flex items-center gap-1.5">
                  <Globe className="h-3 w-3 flex-shrink-0 text-slate-500" />
                  <span className="text-[10px] font-medium text-slate-500">Production</span>
                </div>
                <a
                  href={result.productionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-xs font-medium text-primary hover:underline"
                  title={result.productionUrl}
                >
                  {result.productionUrl.replace(/^https?:\/\//, '')}
                </a>
              </div>

              <div className="min-w-0 overflow-hidden rounded-md border p-2">
                <div className="mb-0.5 flex items-center gap-1.5">
                  <div className="flex-shrink-0 rounded bg-slate-200 px-1 py-0.5 text-[9px] font-bold text-slate-600">
                    DEV
                  </div>
                  <span className="text-[10px] font-medium text-slate-500">Staging</span>
                </div>
                <a
                  href={result.stagingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate font-mono text-xs text-slate-600 hover:underline"
                  title={result.stagingUrl}
                >
                  {result.stagingUrl.replace(/^https?:\/\//, '')}
                </a>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" size="sm" onClick={handleClose} disabled={promoteMutation.isPending}>
                {t('promote.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={handlePromote}
                disabled={
                  promoteMutation.isPending || !customDomain || (!!conflictWarning && !acknowledgeConflict)
                }
              >
                {promoteMutation.isPending ? t('promote.promoting') : t('promote.promote')}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={handleClose} className="w-full">
              {t('promote.close')}
            </Button>
          )}
        </DialogFooter>
        </DryRunSandboxWarning>
      </DialogContent>
    </Dialog>
  );
}
